import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { PageHeader } from "@/components/PageHeader";
import { AudiencePicker } from "@/pages/Campaigns";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CloudCog, Loader2, RefreshCw, Plus, Webhook, Trash2, UploadCloud, ShieldCheck, FolderPlus, Users, ChevronLeft, ChevronRight, Search } from "lucide-react";

function BrevoContactsBrowser({ listId }) {
  const LIMIT = 25;
  const [offset, setOffset] = useState(0);
  const [emailQ, setEmailQ] = useState("");
  const [q, setQ] = useState("");
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["brevo-contacts", listId || "all", offset, q],
    queryFn: async () => {
      if (listId) return (await api.get(`/marketing/brevo/lists/${listId}/contacts`, { params: { limit: LIMIT, offset } })).data;
      return (await api.get("/marketing/brevo/contacts", { params: { limit: LIMIT, offset, email: q } })).data;
    },
    placeholderData: (p) => p,
  });
  const rows = data?.rows || [];
  const count = data?.count || 0;
  return (
    <div data-testid={listId ? `brevo-list-contacts-${listId}` : "brevo-contacts-browser"}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {!listId && (
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={emailQ} onChange={(e) => setEmailQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { setQ(emailQ.trim()); setOffset(0); } }}
              placeholder="Look up exact email, press Enter…" className="pl-8 h-9 bg-white" data-testid="brevo-contacts-search" />
          </div>
        )}
        <p className="text-xs text-slate-400 ml-auto flex items-center gap-2">
          {isFetching && <Loader2 className="h-3 w-3 animate-spin" />}
          {q ? "lookup result" : `${count} contacts in Brevo${listId ? " (this list)" : ""} · read-only`}
        </p>
      </div>
      <Card className="overflow-hidden">
        {isLoading ? <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div> : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              {["Email", "Name", "Company", "City / Zone", "Status", "Lists", "Modified"].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id || c.email} data-testid={`brevo-contact-row-${c.id || c.email}`}>
                  <TableCell className="font-mono text-xs">{c.email}</TableCell>
                  <TableCell className="text-sm">{c.name || <span className="text-slate-300">—</span>}</TableCell>
                  <TableCell className="text-sm text-slate-600">{c.company || <span className="text-slate-300">—</span>}</TableCell>
                  <TableCell className="text-xs text-slate-500">{[c.city, c.zone].filter(Boolean).join(" / ") || "—"}</TableCell>
                  <TableCell>{c.blacklisted
                    ? <span className="text-[11px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 font-semibold">Blocklisted</span>
                    : <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold">Subscribed</span>}</TableCell>
                  <TableCell className="font-mono text-xs">{(c.list_ids || []).length}</TableCell>
                  <TableCell className="text-xs text-slate-500">{c.modified_at ? formatDate(c.modified_at) : "—"}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">{q ? `No Brevo contact with email “${q}”.` : "No contacts."}</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
        {!q && count > LIMIT && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-slate-100">
            <p className="text-xs text-slate-400">{offset + 1}–{Math.min(offset + LIMIT, count)} of {count}</p>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" className="h-7 px-2" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))} data-testid="brevo-contacts-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" className="h-7 px-2" disabled={offset + LIMIT >= count} onClick={() => setOffset(offset + LIMIT)} data-testid="brevo-contacts-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </Card>
      {q && <button onClick={() => { setQ(""); setEmailQ(""); setOffset(0); }} className="text-xs text-brand font-semibold mt-2 hover:underline" data-testid="brevo-contacts-clear-search">← Back to all contacts</button>}
    </div>
  );
}

function StatusCard() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [hookBusy, setHookBusy] = useState(false);
  const [baseUrl, setBaseUrl] = useState("https://salescore.univicoustic.com");
  const [keyOpen, setKeyOpen] = useState(false);
  const [keyVal, setKeyVal] = useState("");
  const [keyBusy, setKeyBusy] = useState(false);
  const [senderOpen, setSenderOpen] = useState(false);
  const [senderForm, setSenderForm] = useState({ email: "", name: "" });
  const [senderBusy, setSenderBusy] = useState(false);
  const { data: snd } = useQuery({ queryKey: ["brevo-sender"], queryFn: async () => (await api.get("/marketing/brevo/sender")).data });
  const saveSender = async () => {
    setSenderBusy(true);
    try {
      const { data } = await api.put("/marketing/brevo/sender", senderForm);
      data.warning ? toast.warning(`Saved — ${data.warning}`) : toast.success(`Catalogues and campaigns now send from ${data.sender.email}`);
      setSenderOpen(false);
      qc.invalidateQueries({ queryKey: ["brevo-sender"] }); qc.invalidateQueries({ queryKey: ["ld-health"] });
    } catch (e) { toast.error(apiError(e)); } finally { setSenderBusy(false); }
  };
  const { data: st } = useQuery({
    queryKey: ["brevo-status"],
    queryFn: async () => (await api.get("/marketing/brevo/status")).data,
  });
  const saveKey = async () => {
    setKeyBusy(true);
    try {
      const { data } = await api.put("/marketing/brevo/api-key", { api_key: keyVal });
      toast.success(`Connected to ${data.account.email} (${data.account.company})`);
      setKeyOpen(false); setKeyVal("");
      qc.invalidateQueries();
    } catch (e) { toast.error(apiError(e)); } finally { setKeyBusy(false); }
  };
  const reconcile = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/brevo/reconcile");
      toast.success(`Reconciled — pushed ${data.pushed_to_brevo} opt-out(s) to Brevo, pulled ${data.pulled_from_brevo.blacklisted + data.pulled_from_brevo.blocked} from Brevo`);
      qc.invalidateQueries({ queryKey: ["brevo-status"] });
      qc.invalidateQueries({ queryKey: ["marketing-optouts"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const setupHook = async () => {
    setHookBusy(true);
    try {
      const { data } = await api.post("/marketing/brevo/setup-webhook", { base_url: baseUrl });
      toast.success(`Webhooks: ${data.webhooks.map((w) => `${w.type} ${w.status}`).join(" · ")}`);
      qc.invalidateQueries({ queryKey: ["brevo-status"] });
    } catch (e) { toast.error(apiError(e)); } finally { setHookBusy(false); }
  };
  return (
    <Card className="p-4 mb-5" data-testid="brevo-status-card">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Brevo account</p>
          <p className="text-sm font-medium">{st?.connected ? <span className="text-emerald-700">● {st.account?.company} ({st.account?.email})</span> : <span className="text-red-600">● Not connected{st?.error ? ` — ${st.error}` : ""}</span>}</p>
          <button className="text-[11px] text-brand hover:underline" onClick={() => setKeyOpen((v) => !v)} data-testid="brevo-set-key-btn">
            {st?.key_source === "app settings" ? "API key set in app · change" : "Set / change API key"}
          </button>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Last opt-out reconcile</p>
          <p className="text-sm">{st?.last_reconcile ? formatDate(st.last_reconcile) : "never"} <span className="text-slate-400 text-xs">(auto every hour)</span></p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Opt-outs pending push</p>
          <p className="text-sm font-mono">{st?.optouts_pending_push ?? "—"}</p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Webhooks</p>
          <p className="text-sm">{(st?.webhooks || []).length ? st.webhooks.map((w) => w.type).join(" + ") : <span className="text-amber-600">not set up</span>}</p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Sending from</p>
          <p className="text-sm font-medium" data-testid="brevo-sender-current">
            {snd?.sender?.email ? <>{snd.sender.email}{snd.verified === true ? <span className="text-emerald-700 text-xs ml-1.5">● verified</span> : snd.verified === false ? <span className="text-red-600 text-xs ml-1.5" title={snd.reason}>● NOT verified in Brevo</span> : null}</> : <span className="text-red-600">not set</span>}
          </p>
          <button className="text-[11px] text-brand hover:underline" onClick={() => { setSenderForm({ email: snd?.sender?.email || "", name: snd?.sender?.name || "" }); setSenderOpen((v) => !v); }} data-testid="brevo-set-sender-btn">Change sender</button>
        </div>
        <div className="flex gap-2 ml-auto">
          <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={reconcile} disabled={busy} data-testid="brevo-reconcile-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Reconcile now</Button>
        </div>
      </div>
      {keyOpen && (
        <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-end gap-2">
          <div className="space-y-1 flex-1 min-w-[300px]">
            <Label className="text-xs">Brevo API key (v3) — validated against Brevo before saving; stored securely in the app, works without server environment access</Label>
            <Input type="password" value={keyVal} onChange={(e) => setKeyVal(e.target.value)} placeholder="xkeysib-…" className="h-8 text-sm font-mono" data-testid="brevo-key-input" />
          </div>
          <Button size="sm" className="h-8 bg-slate-900 hover:bg-slate-800" onClick={saveKey} disabled={keyBusy || !keyVal.trim()} data-testid="brevo-key-save-btn">
            {keyBusy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}Validate &amp; Save</Button>
        </div>
      )}
      {senderOpen && (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-2" data-testid="brevo-sender-form">
          <Label className="text-xs">From address for catalogue emails, campaigns and reminders — must be a verified sender (or on an authenticated domain) in Brevo, else Brevo silently rejects every mail</Label>
          <div className="flex flex-wrap items-end gap-2">
            <Input value={senderForm.email} onChange={(e) => setSenderForm({ ...senderForm, email: e.target.value })} placeholder="marketing@univicoustic.com" className="h-8 text-sm w-[280px]" data-testid="brevo-sender-email-input" />
            <Input value={senderForm.name} onChange={(e) => setSenderForm({ ...senderForm, name: e.target.value })} placeholder="UniVicoustic" className="h-8 text-sm w-[200px]" data-testid="brevo-sender-name-input" />
            <Button size="sm" className="h-8 bg-slate-900 hover:bg-slate-800" onClick={saveSender} disabled={senderBusy || !senderForm.email.trim()} data-testid="brevo-sender-save-btn">
              {senderBusy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}Check with Brevo &amp; Save</Button>
          </div>
          {!!snd?.senders?.length && <p className="text-[11px] text-slate-500">Verified in Brevo: {snd.senders.filter((s) => s.active).map((s) => <button key={s.email} className="font-mono text-brand hover:underline mr-2" onClick={() => setSenderForm({ ...senderForm, email: s.email, name: s.name || senderForm.name })} data-testid={`brevo-sender-pick-${s.email}`}>{s.email}</button>)}{!snd.senders.some((s) => s.active) && "none yet"}</p>}
          {snd?.error && <p className="text-[11px] text-red-600">{snd.error}</p>}
        </div>
      )}
      {(st?.webhooks || []).length < 2 && (
        <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-end gap-2">
          <div className="space-y-1 flex-1 min-w-[260px]">
            <Label className="text-xs flex items-center gap-1"><Webhook className="h-3 w-3" /> Register Brevo webhooks (engagement + opt-out events) — point them at your live app</Label>
            <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} className="h-8 text-sm font-mono" data-testid="brevo-webhook-url" />
          </div>
          <Button size="sm" className="h-8 bg-slate-900 hover:bg-slate-800" onClick={setupHook} disabled={hookBusy} data-testid="brevo-webhook-setup-btn">
            {hookBusy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}Set up webhooks</Button>
        </div>
      )}
    </Card>
  );
}

function SyncAudienceCard({ lists }) {
  const qc = useQueryClient();
  const [source, setSource] = useState("");
  const [filters, setFilters] = useState({});
  const [listId, setListId] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: preview, isFetching } = useQuery({
    queryKey: ["audience-preview", source, JSON.stringify(filters)],
    queryFn: async () => (await api.post("/marketing/audience/preview", { source, filters })).data,
    enabled: !!source,
  });
  const { data: syncs = [] } = useQuery({
    queryKey: ["brevo-syncs"],
    queryFn: async () => (await api.get("/marketing/brevo/syncs")).data,
  });
  const run = async () => {
    if (!source || !listId) { toast.error("Pick an audience and a Brevo list"); return; }
    setBusy(true);
    try {
      const list = lists.find((l) => String(l.id) === String(listId));
      const { data } = await api.post("/marketing/brevo/sync-audience", {
        source, filters, list_id: Number(listId), list_name: list?.name || "",
      });
      toast.success(`${data.count} contact(s) pushed to "${list?.name}" — re-running never duplicates`);
      qc.invalidateQueries({ queryKey: ["brevo-syncs"] });
      qc.invalidateQueries({ queryKey: ["brevo-overview"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="p-5" data-testid="brevo-sync-card">
      <h3 className="font-heading font-semibold text-sm flex items-center gap-2 mb-1"><UploadCloud className="h-4 w-4 text-slate-500" /> Push a CRM audience into a Brevo list</h3>
      <p className="text-[11px] text-slate-400 mb-4">e.g. "all A+ target accounts → list X", "all West-zone dealers → list Y". CRM is the master — contacts are updated, never duplicated. Opted-out addresses are excluded.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AudiencePicker source={source} setSource={setSource} filters={filters} setFilters={setFilters} />
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Target Brevo list *</Label>
            <Select value={listId} onValueChange={setListId}>
              <SelectTrigger className="h-9" data-testid="brevo-sync-list-select"><SelectValue placeholder="Pick a Brevo list" /></SelectTrigger>
              <SelectContent>{lists.map((l) => <SelectItem key={l.id} value={String(l.id)}>{l.name} · {l.unique || l.total} contacts</SelectItem>)}</SelectContent>
            </Select></div>
          {source && (
            <p className="text-sm flex items-center gap-1.5"><Users className="h-4 w-4 text-slate-400" />
              {isFetching ? <Loader2 className="h-4 w-4 animate-spin text-slate-300" /> : <><span className="font-bold font-mono" data-testid="brevo-sync-count">{preview?.count ?? 0}</span> reachable contacts will be pushed</>}</p>
          )}
          <Button onClick={run} disabled={busy || !source || !listId || !(preview?.count)} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="brevo-sync-run-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Sync to Brevo</Button>
        </div>
      </div>
      {syncs.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-100">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Recent syncs</p>
          {syncs.slice(0, 5).map((s) => (
            <p key={s.id} className="text-xs text-slate-500 py-0.5" data-testid={`brevo-sync-row-${s.id}`}>
              <span className="font-medium text-slate-700">{s.count}</span> contacts · {s.source} → <span className="font-medium">{s.list_name || s.list_id}</span> · {s.by} · {formatDate(s.at)}</p>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function BrevoHub() {
  const qc = useQueryClient();
  const [newList, setNewList] = useState(null);
  const [newFolder, setNewFolder] = useState(null);
  const [newAttr, setNewAttr] = useState(null);
  const [viewList, setViewList] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["brevo-overview"],
    queryFn: async () => (await api.get("/marketing/brevo/overview")).data,
  });
  const lists = data?.lists || [];
  const folderName = (id) => (data?.folders || []).find((f) => f.id === id)?.name || id;

  const createList = async () => {
    setBusy(true);
    try {
      await api.post("/marketing/brevo/lists", { name: newList.name, folder_id: newList.folder_id ? Number(newList.folder_id) : undefined });
      toast.success("List created in Brevo"); setNewList(null);
      qc.invalidateQueries({ queryKey: ["brevo-overview"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const createFolder = async () => {
    setBusy(true);
    try {
      await api.post("/marketing/brevo/folders", { name: newFolder.name });
      toast.success("Folder created in Brevo"); setNewFolder(null);
      qc.invalidateQueries({ queryKey: ["brevo-overview"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const createAttr = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/marketing/brevo/attributes", { name: newAttr.name, type: newAttr.type || "text" });
      toast.success(`Attribute ${r.name} created`); setNewAttr(null);
      qc.invalidateQueries({ queryKey: ["brevo-overview"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const delAttr = async (name) => {
    if (!window.confirm(`Delete Brevo attribute ${name}? Its data is removed from all contacts.`)) return;
    try {
      await api.delete(`/marketing/brevo/attributes/${name}`);
      toast.success("Attribute deleted");
      qc.invalidateQueries({ queryKey: ["brevo-overview"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="brevo-hub-page">
      <PageHeader title="Brevo Hub" subtitle="Manage Brevo lists, folders, segments and contact attributes — and keep opt-outs in sync — without logging into Brevo." />
      <StatusCard />

      <Tabs defaultValue="lists">
        <TabsList className="mb-4">
          <TabsTrigger value="lists" data-testid="brevo-tab-lists">Lists</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="brevo-tab-contacts">Brevo Contacts</TabsTrigger>
          <TabsTrigger value="folders" data-testid="brevo-tab-folders">Folders</TabsTrigger>
          <TabsTrigger value="segments" data-testid="brevo-tab-segments">Segments</TabsTrigger>
          <TabsTrigger value="attributes" data-testid="brevo-tab-attributes">Attributes</TabsTrigger>
        </TabsList>

        <TabsContent value="lists">
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
              <p className="text-sm font-semibold">{lists.length} Brevo lists</p>
              <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => setNewList({ name: "" })} data-testid="brevo-new-list-btn"><Plus className="h-3.5 w-3.5" /> New list</Button>
            </div>
            {isLoading ? <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div> : (
              <Table>
                <TableHeader><TableRow className="bg-slate-50">{["List", "Folder", "Subscribed", "Total contacts"].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {lists.map((l) => (
                    <TableRow key={l.id} className="cursor-pointer hover:bg-orange-50/40" onClick={() => setViewList(l)} data-testid={`brevo-list-row-${l.id}`}>
                      <TableCell className="font-medium">{l.name} <span className="text-[10px] text-slate-400 font-mono">#{l.id}</span></TableCell>
                      <TableCell className="text-slate-500">{folderName(l.folder_id)}</TableCell>
                      <TableCell className="font-mono">{l.total}</TableCell>
                      <TableCell className="font-mono">{l.unique}</TableCell>
                    </TableRow>
                  ))}
                  {lists.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-400">No lists yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="contacts">
          <BrevoContactsBrowser />
        </TabsContent>

        <TabsContent value="folders">
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
              <p className="text-sm font-semibold">{(data?.folders || []).length} folders</p>
              <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => setNewFolder({ name: "" })} data-testid="brevo-new-folder-btn"><FolderPlus className="h-3.5 w-3.5" /> New folder</Button>
            </div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50">{["Folder", "Total contacts", "Unique"].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {(data?.folders || []).map((f) => (
                  <TableRow key={f.id} data-testid={`brevo-folder-row-${f.id}`}>
                    <TableCell className="font-medium">{f.name} <span className="text-[10px] text-slate-400 font-mono">#{f.id}</span></TableCell>
                    <TableCell className="font-mono">{f.total_subscribers}</TableCell>
                    <TableCell className="font-mono">{f.unique_subscribers}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="segments">
          <Card className="overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">{["Segment", "Category"].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {(data?.segments || []).map((s) => (
                  <TableRow key={s.id}><TableCell className="font-medium">{s.name}</TableCell><TableCell className="text-slate-500">{s.category || "—"}</TableCell></TableRow>
                ))}
                {(data?.segments || []).length === 0 && <TableRow><TableCell colSpan={2} className="text-center py-8 text-slate-400">No segments — segments are built inside Brevo from contact attributes; they'll show here automatically.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="attributes">
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
              <p className="text-sm font-semibold">{(data?.attributes || []).length} contact attributes</p>
              <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => setNewAttr({ name: "", type: "text" })} data-testid="brevo-new-attr-btn"><Plus className="h-3.5 w-3.5" /> New attribute</Button>
            </div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50">{["Attribute", "Category", "Type", ""].map((h, i) => <TableHead key={i} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {(data?.attributes || []).map((a) => (
                  <TableRow key={a.name} data-testid={`brevo-attr-row-${a.name}`}>
                    <TableCell className="font-mono text-sm font-medium">{a.name}</TableCell>
                    <TableCell className="text-slate-500 capitalize">{a.category}</TableCell>
                    <TableCell className="text-slate-500">{a.type || "—"}</TableCell>
                    <TableCell className="text-right">
                      {a.category === "normal" && (
                        <button onClick={() => delAttr(a.name)} className="text-slate-300 hover:text-red-600 p-1" data-testid={`brevo-attr-del-${a.name}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="mt-6">
        <SyncAudienceCard lists={lists} />
      </div>

      <p className="text-[11px] text-slate-400 mt-4 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" />
        Ownership: the CRM is the master for customer data (pushes to Brevo) · Brevo is the master for opt-outs & engagement (pushed back hourly + live via webhook).</p>

      {/* create dialogs */}
      <Dialog open={!!viewList} onOpenChange={(o) => !o && setViewList(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="brevo-list-contacts-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{viewList?.name} — contacts</DialogTitle>
            <DialogDescription>Read-only view of this Brevo list. Nothing here is merged into CRM contacts.</DialogDescription>
          </DialogHeader>
          {viewList && <BrevoContactsBrowser listId={viewList.id} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!newList} onOpenChange={(o) => !o && setNewList(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="brevo-list-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Brevo List</DialogTitle><DialogDescription>Created directly in your Brevo account.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">List name *</Label>
              <Input value={newList?.name || ""} onChange={(e) => setNewList({ ...newList, name: e.target.value })} data-testid="brevo-list-name-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Folder</Label>
              <Select value={newList?.folder_id || "__auto"} onValueChange={(v) => setNewList({ ...newList, folder_id: v === "__auto" ? "" : v })}>
                <SelectTrigger className="h-9" data-testid="brevo-list-folder-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__auto">Automatic</SelectItem>{(data?.folders || []).map((f) => <SelectItem key={f.id} value={String(f.id)}>{f.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setNewList(null)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={createList} disabled={busy || !newList?.name?.trim()} data-testid="brevo-list-create-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!newFolder} onOpenChange={(o) => !o && setNewFolder(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="brevo-folder-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Brevo Folder</DialogTitle></DialogHeader>
          <div className="space-y-1.5"><Label className="text-xs">Folder name *</Label>
            <Input value={newFolder?.name || ""} onChange={(e) => setNewFolder({ name: e.target.value })} data-testid="brevo-folder-name-input" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setNewFolder(null)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={createFolder} disabled={busy || !newFolder?.name?.trim()} data-testid="brevo-folder-create-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!newAttr} onOpenChange={(o) => !o && setNewAttr(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="brevo-attr-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Contact Attribute</DialogTitle><DialogDescription>A custom field on every Brevo contact (e.g. ZONE, DEALER_NAME).</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Name * (UPPERCASE, no spaces)</Label>
              <Input value={newAttr?.name || ""} onChange={(e) => setNewAttr({ ...newAttr, name: e.target.value.toUpperCase() })} placeholder="e.g. ACCOUNT_CLASS" data-testid="brevo-attr-name-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Type</Label>
              <Select value={newAttr?.type || "text"} onValueChange={(v) => setNewAttr({ ...newAttr, type: v })}>
                <SelectTrigger className="h-9" data-testid="brevo-attr-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["text", "date", "float", "boolean"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setNewAttr(null)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={createAttr} disabled={busy || !newAttr?.name?.trim()} data-testid="brevo-attr-create-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
